import { useEffect, type ReactNode } from 'react';

export function Drawer({
  open,
  onClose,
  title,
  eyebrow,
  children,
  footer,
  wide,
  actions,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  eyebrow?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Painel largo, para conteúdo que pede duas colunas. Quem usa decide (e pode oferecer o botão em `actions`). */
  wide?: boolean;
  /** Ações no cabeçalho, antes do fechar (ex.: expandir). */
  actions?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <>
      <div className="rr-drawer-overlay" onClick={onClose} />
      <aside className={wide ? 'rr-drawer rr-drawer--wide' : 'rr-drawer'} role="dialog" aria-modal="true">
        <header className="rr-drawer__head">
          <div className="rr-stack rr-stack--2">
            {eyebrow && <span className="rr-label">{eyebrow}</span>}
            <h2 className="rr-heading">{title}</h2>
          </div>
          {actions && <div className="rr-drawer__actions">{actions}</div>}
          <button className="rr-close" onClick={onClose} aria-label="Fechar" type="button">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
          </button>
        </header>
        <div className="rr-drawer__body">{children}</div>
        {footer && <footer className="rr-drawer__foot">{footer}</footer>}
      </aside>
    </>
  );
}

export function Segmented<T extends string>({ options, value, onChange }: { options: { value: T; label: string }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="rr-segmented" role="group">
      {options.map((o) => (
        <button key={o.value} type="button" className="rr-segmented__opt" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Janela no centro da tela, para acompanhar algo que demora. `onClose` ausente = não fecha por fora (espera terminar). */
/** `huge`: quase a tela inteira, para detalhe com imagem grande e muita informação (a imagem da galeria). */
export function Modal({ open, onClose, title, eyebrow, children, footer, wide, huge }: { open: boolean; onClose?: () => void; title: ReactNode; eyebrow?: string; children: ReactNode; footer?: ReactNode; wide?: boolean; huge?: boolean }) {
  useEffect(() => {
    if (!open || !onClose) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="rr-modal__backdrop" onClick={onClose}>
      <div className={huge ? 'rr-modal rr-modal--huge' : wide ? 'rr-modal rr-modal--wide' : 'rr-modal'} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <header className="rr-modal__head">
          <div>{eyebrow && <span className="rr-label rr-label--accent">{eyebrow}</span>}<h2 className="rr-modal__title">{title}</h2></div>
          {onClose && <button type="button" className="rr-budget__toggle" style={{ fontSize: 'var(--rr-text-xs)' }} onClick={onClose}>fechar</button>}
        </header>
        <div className="rr-modal__body">{children}</div>
        {footer && <footer className="rr-modal__foot">{footer}</footer>}
      </div>
    </div>
  );
}
